import { pushRoute } from '../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import StakeholdersProfileHeader from '../../components/headers/PageHeaders/Project/Stakeholders/StakeholdersProfileHeader.jsx';
import LastUpdateIcon from '../../components/icons/LastUpdateIcon.jsx';
import StakeholderProfile from '../../components/projects/Stakeholders/StakeholderProfile.jsx';
import StakeholderProfileActionsDropdown from '../../components/projects/Stakeholders/StakeholderProfileActionsDropdown.jsx';
import StakeholdersHistoryModal from '../../components/projects/Stakeholders/StakeholdersHistoryModal.jsx';
import SubHeader from '../../components/SubHeader.jsx';
import { retrieveEmployees } from '../../modules/employees.js';
import {
  retrieveProject,
  retrieveProjects,
  selectProject,
  selectProjects,
} from '../../modules/projects.js';
import {
  deleteStakeholder,
  retrieveStakeholders,
  selectStakeholder,
} from '../../modules/stakeholders.js';
import {
  retrieveStakeholderHistory,
  selectStakeholderHistory,
} from '../../modules/stakeholdersHistory.js';
import Empty from '../../components/Empty.jsx';
import AuthorizedAction from '../../components/AuthorizedAction.js';
import {
  retrieveGroupParticipations,
  selectGroupParticipations,
} from '../../modules/groupParticipations.js';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import ProjectsSelect from '../../components/projects/Stakeholders/ProjectsSelect.jsx';
import { loadingContent } from '../../components/EmptyStates.jsx';
import { getOrgTermsContent } from '../../content/utils.js';

const mapStateToProps = (state, ownProps) => ({
  project: selectProject(ownProps.projectId)(state),
  projects: selectProjects(state),
  projectId: ownProps.projectId,
  changeHistory: selectStakeholderHistory(state, ownProps),
  apiUrl: state.config.apiUrl,
  stakeholder: selectStakeholder(state, ownProps),
  groupParticipations: selectGroupParticipations(state, ownProps),
});

const mapDispatchToProps = {
  retrieveProject,
  retrieveStakeholderHistory,
  deleteStakeholder,
  retrieveStakeholders,
  retrieveEmployees,
  retrieveGroupParticipations,
  retrieveProjects,
};

const StakeholderProfileContainer = (props) => {
  const {
    slug,
    projectId,
    stakeholderId,
    project,
    projects,
    changeHistory,
    retrieveStakeholderHistory,
    retrieveProjects,
    deleteStakeholder,
    retrieveStakeholders,
    retrieveGroupParticipations,
    groupParticipations,
    stakeholder,
    apiUrl,
  } = props;

  const { loading, downloadBlob } = useFetchFile();
  const [isLoading, setIsLoading] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const onShowHistoryModal = () => setShowHistoryModal(true);
  const onHideHistoryModal = () => setShowHistoryModal(false);

  const navigateToStakeholderAnalysis = () =>
    pushRoute('projects/stakeholders', { projectId });

  const onProjectChange = (relatedProject) => {
    setIsLoading(true);
    return pushRoute('projects/stakeholders/show', {
      projectId: relatedProject.project_id,
      stakeholderId: relatedProject.stakeholder_id,
    });
  };

  const onDownloadErrorsCsv = () => {
    downloadBlob(
      apiUrl,
      `stakeholders/${stakeholderId}/group_structure_errors_csv`,
      null,
      'current-group-participation CSV',
      'csv'
    );
  };

  const onDownloadReport = () => {
    downloadBlob(
      apiUrl,
      `stakeholders/${stakeholderId}/profile_pdf`,
      null,
      `${stakeholder.name}_stakeholder-report`,
      'pdf'
    );
  };

  const onDelete = () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this stakeholder profile?'
      )
    )
      return;
    deleteStakeholder(stakeholderId).then(() =>
      navigateToStakeholderAnalysis()
    );
  };

  useEffect(() => {
    Promise.all([retrieveProjects(), retrieveStakeholders(projectId)]).then(
      () => setIsLoading(false)
    );

    retrieveStakeholderHistory(projectId, stakeholderId);
    retrieveGroupParticipations(stakeholderId);
  }, [stakeholderId, projectId]);

  if (isLoading) return <Empty simplified content={loadingContent} />;
  return (
    <React.Fragment>
      <StakeholdersProfileHeader
        project={project}
        stakeholder={stakeholder}
        slug={slug}
      />
      <SubHeader flexJustify small>
        <div className="flex">
          <h2>Stakeholder Profile: {stakeholder.name}</h2>

          {stakeholder ? (
            <strong className="header-item bordered text-muted">
              {getOrgTermsContent('projects')}:&nbsp;
              <span className="text-primary">
                {stakeholder.related_projects
                  ? stakeholder.related_projects.length + 1
                  : 1}
              </span>
            </strong>
          ) : null}

          {changeHistory?.length ? (
            <React.Fragment>
              <Button
                aria-label="View last update"
                type="button"
                onClick={onShowHistoryModal}
                className="flex flex-align-center header-item btn-last-update-icon m-r-8"
              >
                <LastUpdateIcon />
              </Button>
              <StakeholdersHistoryModal
                slug={slug}
                show={showHistoryModal}
                onHide={onHideHistoryModal}
                history={changeHistory || []}
                project={project}
              />
            </React.Fragment>
          ) : null}
        </div>

        <div className="flex flex-1 flex-end">
          {stakeholder.related_projects ? (
            <ProjectsSelect
              relatedProjects={stakeholder.related_projects}
              projects={projects}
              project={project}
              onProjectChange={onProjectChange}
            />
          ) : null}
          <div className="flex m-l-12">
            <AuthorizedAction action="update" subject="stakeholder">
              <div>
                <StakeholderProfileActionsDropdown
                  slug={slug}
                  projectId={projectId}
                  stakeholderId={stakeholderId}
                  project={project}
                  isReportLoading={loading}
                  onDownloadReport={onDownloadReport}
                  onDelete={onDelete}
                  isArchived={project.archived}
                />
              </div>
            </AuthorizedAction>
          </div>
        </div>
      </SubHeader>
      {stakeholder.group_structure_errors ? (
        <div className="component-xl component-border-bottom text-center">
          <h3 className="text-danger">There was an issue with the CSV.</h3>
          <br />
          <AuthorizedAction action="update" subject="stakeholder">
            <p className="no-margin">
              Please download the CSV error report
              <button className="btn-link" onClick={onDownloadErrorsCsv}>
                here
              </button>
            </p>
          </AuthorizedAction>
        </div>
      ) : null}
      <StakeholderProfile
        project={project}
        onShowHistoryModal={onShowHistoryModal}
        stakeholder={stakeholder}
        groupParticipations={groupParticipations}
      />
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StakeholderProfileContainer);
